// #region: Class definition *********************************************************************/
/**
 * @class MarkerSelector
 * @description Custom web component that renders a viewer with marker logic.
 * Only one marker per inspection is allowed.
 *
 * @extends PdfViewer
 * Extends the pdf-viewer web component.
 *
 * @example
 * <marker-selector src="file-to-load" inspection-id="1" blueprint-id="1">
 *    <div class="loader k-loading-mask">
 *       <div class="k-loading-image"></div>
 *    </div>
 * </marker-selector>
 *
 * @property {string} src - The path to the view.
 * @property {string} blueprint-id - The unique identifier for the current blueprint.
 *
 * @remarks
 *     The attributes are optional.
 *     Only one marker is allowed per selector.
*/
class MarkerSelector extends PdfViewer {

    // #region: Static Data **********************************************************************/
    /**
     * Upper bound on detail tiles retained (detached but already-decoded) after they scroll out of the visible region. Scrolling back re-attaches a
     * retained tile instantly instead of mounting a fresh `<img>` that must re-decode — which is what briefly flashed the blurred base layer back in.
     * At a 256px tile (~256KB decoded RGBA) this cap bounds the retention cache to roughly 40MB, which keeps several screens' worth warm without
     * approaching the per-tab memory ceiling on phones.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_CACHE_MAX = 150;

    /**
     * Upper bound on the device pixel ratio used when choosing a tile pyramid level. Capping at 2 keeps the on-screen tile count (and image-decode
     * memory) sane on DPR-3 phones — selecting by the raw ratio fetches a level with ~2× the tiles per axis for sharpness the eye can't resolve on a
     * blueprint. Deep zoom still reaches the full-resolution level because the requested width there exceeds every level regardless of this cap.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_MAX_DPR = 2;

    /**
     * Number of extra tile rings rendered outside the visible region. Picks up tiles the user is about to scroll into without paying the full-page cost.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_OVERSCAN = 1;

    /**
     * Poll interval (ms) used while blueprint tiles are still being generated on the server.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_POLL_MS = 1500;

    /**
     * Upper bound on the fake-progress creep. Holds the bar short of 100% so real status updates always have somewhere to go and the bar never
     * "completes" before the actual generation does.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_PROGRESS_FAKE_CAP = 90;

    /**
     * Tick interval (ms) for the fake-progress creep that runs before the server reports real tile-generation status. Picks up immediately after the
     * overlay shows so the user sees motion from frame 0 instead of a static 0% bar.
     * @type {number}
     * @static
     * @memberof MarkerSelector
     */
    static TILE_PROGRESS_FAKE_TICK_MS = 250;
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * The unique identifier for the current blueprint being viewed.
     * @type {number|null}
     * 
     * @private
     */
    #blueprintId = null;

    /**
     * LRU cache of detail tiles that scrolled out of view, keyed identically to `#detailTiles` (`${pageNumber}/${level}/${tx}/${ty}`). Tiles are detached
     * from the DOM but kept referenced so their decoded bitmap survives; re-mounting on scroll-back reuses the decoded `<img>` instantly rather than
     * creating a fresh one that re-decodes (which flashed the blurred base layer). Insertion order is the LRU order; capped at `TILE_CACHE_MAX`.
     * @type {Map<string, HTMLImageElement>}
     *
     * @private
     */
    #detailTileCache = new Map();

    /**
     * Currently-mounted detail tiles, keyed by `${pageNumber}/${level}/${tx}/${ty}`. Lets the viewport update mount only newly-visible tiles and
     * unmount tiles that scrolled out, instead of rebuilding the whole layer on every pan.
     * @type {Map<string, HTMLImageElement>}
     *
     * @private
     */
    #detailTiles = new Map();

    /**
     * Stores the currently dragged marker element.
     * @type {HTMLElement|null}
     * 
     * @private
     */
    #draggedMarker = null;

    /**
     * Pointer X at drag start, in unscaled page coordinates (see #clientToPageCoords). The move handler subtracts this to get a 1:1 page-space delta.
     * @type {number}
     *
     * @private
     */
    #dragStartX = 0;

    /**
     * Pointer Y at drag start, in unscaled page coordinates (see #clientToPageCoords). The move handler subtracts this to get a 1:1 page-space delta.
     * @type {number}
     *
     * @private
     */
    #dragStartY = 0;

    /**
     * The CSS class name for the marker icon.
     * @type {string}
     *
     * @private
     */
    #iconClass = 'lf-icon-location-on';

    /**
     * Flag indicating if the component is currently in marker placing mode.
     * @type {boolean}
     *
     * @private
     */
    #isPlacingMarker;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     *
     * @private
     */
    #locales;

    /**
     * The marker button for adding/deleting marker.
     * @type {HTMLButtonElement|null}
     *
     * @private
     */
    #markerButton;

    /**
     * The marker data object for the marker.
     * @type {Object|null}
     *
     * @private
     */
    #markerData = null;

    /**
     * Stores the id of the single marker if in single-marker mode.
     * @type {string|null}
     * 
     * @private
     */
    #markerId = null;

    /**
     * The DOM element that serves as a layer on top of the view to hold the marker.
     * @type {HTMLDivElement|null}
     *
     * @private
     */
    #markerLayer;

    /**
     * Stores the marker size, calculated once from the css values.
     * @type {Object}
     * 
     * @private
     */
    #markerRect;

    /**
     * Stores the initial X coordinate of a marker before dragging.
     * @type {number}
     * 
     * @private
     */
    #markerStartX = 0;

    /**
     * Stores the initial Y coordinate of a marker before dragging.
     * @type {number}
     * 
     * @private
     */
    #markerStartY = 0;

    /**
     * Whole-page fallback tile layer, mounted at the cheapest pyramid level. Sits beneath `#tileDetailLayer` so a region still streaming its sharp
     * tiles (during a pan or a level switch) shows the low-res page rather than blank space. Rebuilt only when the page changes.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileBaseLayer = null;

    /**
     * Page number whose base layer is currently mounted, or null. Guards `#ensureBaseTileLayer` so the whole-page fallback is rebuilt only on a real
     * page change.
     * @type {?number}
     *
     * @private
     */
    #tileBasePage = null;

    /**
     * Tile-endpoint context parsed from the `blueprint-id` attribute or the `src` URL when tile mode is active.
     * @type {?{blueprintId: number, inspectionGuid: ?string}}
     *
     * @private
     */
    #tileContext = null;

    /**
     * High-resolution tile layer holding the visible region (plus an overscan ring) at the zoom-appropriate pyramid level. Tiles are mounted/unmounted
     * as the viewport changes; positioned in percentages so they scale with the wrapper during pinch without per-frame JS. No canvas is involved, so the
     * device canvas-pixel limit (the iOS crash on large blueprints) never applies.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileDetailLayer = null;

    /**
     * Interval id for the fake-progress creep that runs while we wait for the server's first real status update. Cleared when the real status arrives
     * (or when the overlay is hidden) so the bar transitions cleanly from fake → real progress.
     * @type {?number}
     *
     * @private
     */
    #tileFakeProgressTimer = null;

    /**
     * Current fake-progress value (0–100). Held outside the DOM so the timer can resume from the last drawn value without parsing the bar's CSS width
     * back from the layout. Doubles as the value rendered into the `{2}%` slot of the progress label so the bar and label always agree.
     * @type {number}
     *
     * @private
     */
    #tileFakeProgressValue = 0;

    /**
     * Per-page manifests returned by `/api/blueprinttile/manifest/{id}` when tiles are ready.
     * @type {?Array<{pageNumber: number, width: number, height: number, maxZoom: number, tileSize: number, pdfWidthPts: number, pdfHeightPts: number}>}
     *
     * @private
     */
    #tileManifest = null;

    /**
     * Terminal-message overlay shown when the blueprint cannot be displayed (e.g. tile generation failed on iOS, where falling back to a full-page canvas
     * render would exceed the device's canvas limit). Mutually exclusive with the progress overlay and the tile layers.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileMessageOverlay = null;

    /**
     * Flag indicating whether the current source is being rendered from server-generated tiles. False means the PDF.js code path is used.
     * @type {boolean}
     *
     * @private
     */
    #tileMode = false;

    /**
     * rAF id coalescing the per-frame detail-tile mount that runs while a pan/scroll gesture is in flight.
     * Holds at most one pending frame so a burst of `panzoompan` events collapses into a single update per frame.
     * @type {?number}
     *
     * @private
     */
    #tilePanFrame = null;

    /**
     * Timer id for the active tile-generation polling loop, or null when no job is being tracked.
     * @type {?number}
     *
     * @private
     */
    #tilePollTimer = null;

    /**
     * Progress bar fill inside the progress overlay.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileProgressBar = null;

    /**
     * Label inside the progress overlay.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileProgressLabel = null;

    /**
     * Progress overlay shown while tiles are being generated server-side.
     * @type {?HTMLDivElement}
     *
     * @private
     */
    #tileProgressOverlay = null;

    /**
     * Debounce timer for the pan-only detail-tile viewport update. Coalesces the burst of events at the end of a drag into a single mount/unmount pass.
     * @type {?number}
     *
     * @private
     */
    #tileViewportTimer = null;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of MarkerSelector.
     */
    constructor() {
        super();

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();

        // Build the tile layers + progress overlay now (before any load) so the render-strategy hooks always have their DOM to work with.
        this.#createTileElements();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof MarkerSelector
     */
    static tagName = 'marker-selector';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     */
    toString() {
        return '[MarkerSelector]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     */
    static toString() {
        return '[MarkerSelector]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     */
    static version() {
        return '1.1.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe.
     *
     * @static
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'blueprint-id', 'icon-class', 'inspection-id'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the current blueprint id.
     * @returns {string|null} The blueprint id or null if not set.
     */
    get blueprintId() {
        return this.#blueprintId;
    }

    /**
     * Sets the blueprint id.
     * @param {string|null} value The blueprint id to set.
     */
    set blueprintId(value) {
        if (this.#blueprintId !== value) {
            this.#blueprintId = value;

            // Update the attribute to keep it in sync.
            if (value) {
                this.setAttribute('blueprint-id', value);
            }
            else {
                this.removeAttribute('blueprint-id');
            }
        }

        this.#markerButton.removeAttribute('disabled');
    }

    /**
     * Gets the marker icon class.
     * @returns {string} The CSS class name for the marker icon.
     */
    get iconClass() {
        return this.#iconClass;
    }

    /**
     * Sets the marker icon class.
     * @param {string} value The CSS class name for the marker icon.
     */
    set iconClass(value) {
        if (this.#iconClass !== value) {
            this.#iconClass = value;

            // Update the attribute to keep it in sync.
            if (value) {
                this.setAttribute('icon-class', value);
            }
            else {
                this.removeAttribute('icon-class');
            }
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous value of the attribute.
     * @param {string|null} newValue The new value of the attribute.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Call parent's implementation.
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'blueprint-id':
                this.blueprintId = parseInt(newValue, 10);

                break;

            case 'icon-class':
                this.iconClass = newValue;

                break;

            case 'read-only':
                // Base class handles the attribute, we just apply the state.
                this.#applyReadOnlyState();

                break;

            default:
                break;

        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * Extends to setup marker-related components.
     */
    connectedCallback() {
        super.connectedCallback();

        // Get blueprint id from the attribute.
        this.#blueprintId = parseInt(this.getAttribute('blueprint-id'), 10);

        // Get icon class from the attribute if provided.
        const iconClassAttr = this.getAttribute('icon-class');

        if (iconClassAttr) {
            this.#iconClass = iconClassAttr;
        }
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * Extend to clean up marker-related resources.
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Remove event listeners.
        if (this.#markerLayer) {
            this.#markerLayer.removeEventListener('click', this.handleMarkerAdd);
            this.#markerLayer.removeEventListener('touchend', this.handleMarkerAdd);
        }

        // Clean up any active marker drag.
        if (this.#draggedMarker) {
            document.removeEventListener('mousemove', this.handleMarkerMove);
            document.removeEventListener('mouseup', this.handleMarkerUp);
            document.removeEventListener('touchmove', this.handleMarkerMove);
            document.removeEventListener('touchend', this.handleMarkerUp);

            this.#draggedMarker = null;
        }
    }

    /**
     * Initialize the UI (on 'DOMContentLoaded').
     * 
     * @memberof MarkerSelector
     */
    initialize() {
        this.#setupMarkers();
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles the click or touch event on the marker layer to add a new marker.
     * Also handles the click event on the marker add button.
     * @param {MouseEvent|TouchEvent} event The click or touch event.
     */
    handleMarkerAdd = (event) => {
        // Only proceed to add a marker if in placing mode and marker layer exists.
        if (!this.#isPlacingMarker || !this.#markerLayer) {
            return;
        }

        // Prevent adding marker if we just finished dragging.
        if (this.#draggedMarker) {
            return;
        }

        // Stop propagation.
        event.stopPropagation();
        event.preventDefault();

        // Determine if this is a touch event.
        const isTouch = event.type === 'touchend';
        const touch = isTouch ? event.changedTouches[0] : event;

        // Convert the click to unscaled page coordinates straight off the rendered layer (not currentScale), so the marker lands exactly under the
        // pointer at any zoom level. See #clientToPageCoords for why currentScale can't be used here.
        const coords = this.#clientToPageCoords(touch.clientX, touch.clientY);

        if (!coords) {
            return;
        }

        // Create new marker.
        this.addMarker({
            blueprintId: this.#blueprintId,
            id: null,
            inspectionId: null,
            pageNumber: this.currentPage,
            x: coords.x,
            y: coords.y
        });

        // Exit marker placement mode.
        this.setPlacementMode(false);
    };

    /**
     * Handle marker mousedown/touchstart to initiate dragging.
     * @param {MouseEvent|TouchEvent} event The mouse/touch event data.
     */
    handleMarkerDown = (event) => {
        // Don't allow marker dragging in read-only mode.
        if (this.readOnly) {
            return;
        }

        // A multi-touch gesture is a pinch-zoom, not a marker drag. Let it through to the viewer instead of hijacking it — otherwise the finger that
        // happens to land on the marker drags it along with the zoom and corrupts the marker's stored position.
        if (event.type === 'touchstart' && event.touches.length > 1) {
            return;
        }

        // Stop propagation.
        event.stopPropagation();
        event.preventDefault();

        const markerElement = event.currentTarget;
        const isTouch = event.type === 'touchstart';

        // Find the marker data associated with this marker element.
        if (!this.#markerData || this.#markerData.marker !== markerElement) {
            return;
        }

        // Get the initial pointer position in unscaled page coordinates (the same space the marker is stored and rendered in).
        const clientX = isTouch ? event.touches[0].clientX : event.clientX;
        const clientY = isTouch ? event.touches[0].clientY : event.clientY;

        const start = this.#clientToPageCoords(clientX, clientY);

        if (!start) {
            return;
        }

        this.#draggedMarker = this.#markerData;

        // Store the drag anchor and the marker's starting position, both in unscaled page coordinates, so the move handler can apply a 1:1 delta.
        this.#dragStartX = start.x;
        this.#dragStartY = start.y;
        this.#markerStartX = this.#markerData.x;
        this.#markerStartY = this.#markerData.y;

        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const upEvent = isTouch ? 'touchend' : 'mouseup';

        // Add event listeners for mousemove and mouseup on the document.
        document.addEventListener(moveEvent, this.handleMarkerMove, { passive: false });
        document.addEventListener(upEvent, this.handleMarkerUp, { passive: false });

        markerElement.classList.add('dragging');
    };

    /**
     * Handles marker move during drag.
     * @param {MouseEvent|TouchEvent} event The mouse/touch event data.
     * 
     * @private
     */
    handleMarkerMove = (event) => {
        if (!this.#draggedMarker) {
            return;
        }

        // A second touch appearing mid-drag means the gesture became a pinch-zoom. Stop following the pointer (and let the event reach the viewer) so
        // the zoom isn't turned into a marker drag.
        if (event.type === 'touchmove' && event.touches.length > 1) {
            return;
        }

        // Stop propagation.
        event.stopPropagation();
        event.preventDefault();

        // Get current pointer position in unscaled page coordinates.
        const isTouch = event.type === 'touchmove';
        const clientX = isTouch ? event.touches[0].clientX : event.clientX;
        const clientY = isTouch ? event.touches[0].clientY : event.clientY;

        const current = this.#clientToPageCoords(clientX, clientY);

        if (!current) {
            return;
        }

        // Apply the pointer delta 1:1 in page coordinates.
        this.#draggedMarker.x = this.#markerStartX + (current.x - this.#dragStartX);
        this.#draggedMarker.y = this.#markerStartY + (current.y - this.#dragStartY);

        // Update visual position.
        this.#updateMarkerPosition(this.#draggedMarker);
    };

    /**
     * Handles marker mouseup/touchend to end drag.
     * @param {MouseEvent|TouchEvent} event The mouse/touch event data.
     *
     * @private
     */
    handleMarkerUp = (event) => {
        if (!this.#draggedMarker) {
            return;
        }

        // Stop propagation.
        event.stopPropagation();
        event.preventDefault();

        // Remove dragging class.
        this.#draggedMarker.marker.classList.remove('dragging');

        // Update final marker position.
        this.#updateMarkerPosition(this.#draggedMarker);

        // Dispatch marker moved event.
        this.dispatchEvent(new CustomEvent('markerMoved', {
            detail: {
                blueprintId: this.#draggedMarker.blueprintId || this.#blueprintId,
                id: this.#draggedMarker.id,
                inspectionId: this.#draggedMarker.inspectionId,
                oldX: this.#markerStartX,
                oldY: this.#markerStartY,
                pageNumber: this.#draggedMarker.pageNumber,
                x: this.#draggedMarker.x,
                y: this.#draggedMarker.y
            }
        }));

        // Clean up.
        const isTouch = event.type === 'touchend';
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const upEvent = isTouch ? 'touchend' : 'mouseup';

        document.removeEventListener(moveEvent, this.handleMarkerMove);
        document.removeEventListener(upEvent, this.handleMarkerUp);

        // Reset after a delay to prevent click event.
        setTimeout(() => {
            this.#draggedMarker = null;
        }, 100);
    };
    // #endregion ********************************************************************************/

    // #region: Public Methods ******************************************************************/
    /**
     * Renders the marker on the page.
     * @param {object} markerData The BlueprintMarker entity data object.
     * @param {string} [markerData.id] Optional id for the marker. A unique id will be generated if not provided.
     * @param {number} [markerData.blueprintId] The blueprint id associated with this marker. Falls back to current blueprint id if not provided.
     * @param {number} [markerData.inspectionId] The inspection id associated with this marker.
     * @param {number} markerData.pageNumber The page number where the marker should be placed.
     * @param {number} markerData.x The unscaled X coordinate of the marker.
     * @param {number} markerData.y The unscaled Y coordinate of the marker.
     * @param {HTMLElement} [markerData.marker] The DOM element for the marker (internal use).
     *
     * @fires CustomEvent#markerAdded
     * @memberof MarkerSelector
     */
    addMarker(markerData) {
        // Check for valid marker data.
        if (!markerData || !this.#markerLayer || (markerData.id && this.#markerData && this.#markerData.id === markerData.id) || markerData.pageNumber > this.totalPages) {
            return;
        }

        // Ensure blueprint/inspection id's are set, either from marker data or current blueprint/inspection.
        if (!markerData.blueprintId) {
            markerData.blueprintId = this.#blueprintId;
        }

        // Ensure only one marker is added if in single marker mode: remove existing marker if present.
        if (this.#markerId) {
            // Remove existing marker without dispatching event.
            this.removeMarker(this.#markerId, false);
        }

        // Add a (hidden) marker, will be shown in #updateMarkerPosition method.
        const marker = document.createElement('div');
        marker.className = 'location-marker disable-panzoom';
        marker.innerHTML = `<span class="marker-icon ${this.#iconClass}"></span>`;
        marker.classList.add('hidden');

        // Generate unique id for the marker if not provided.
        const id = markerData.id || `marker-${Date.now()}`;
        const isNewMarker = !markerData.id;

        markerData.id = id;
        markerData.marker = marker;

        // Store marker data for future reference.
        this.#markerData = markerData;

        // Store the id of the newly added marker.
        this.#markerId = id;
        this.#markerButton.setAttribute('disabled', '');

        // Update button to show delete state.
        this.#updateMarkerButton();

        // Add marker to the marker layer.
        this.#markerLayer.appendChild(marker);

        this.#updateMarkerPosition(markerData);

        // Configure marker drag handling and styling based on read-only state.
        if (!this.readOnly) {
            // Add both mouse and touch support.
            marker.addEventListener('mousedown', this.handleMarkerDown, { passive: false });
            marker.addEventListener('touchstart', this.handleMarkerDown, { passive: false });
        }

        // When a new marker is added, signal any listeners.
        if (isNewMarker) {
            this.#dispatchMarkerEvent('markerAdded', markerData);
        }
    }

    /**
     * Clears the marker and resets the component state.
     * Removes the marker from the display and internal state.
     * @returns {void}
     *
     * @memberof MarkerSelector
     */
    clear() {
        // Reset marker.
        this.removeMarker(this.#markerId, true);
        this.#markerId = null;

        // Clear internal marker and layer.
        this.#markerData = null;

        if (this.#markerLayer) {
            this.#markerLayer.innerHTML = '';
        }

        // Reset drag state completely.
        this.#draggedMarker = null;

        // Don't enter placement mode automatically - user needs to click add button.
        this.setPlacementMode(false);
    }

    /**
     * Gets the current marker data.
     * @returns {Object|null} The marker data object or null if no marker exists.
     *
     * @memberof MarkerSelector
     */
    getMarkerData() {
        if (!this.#markerData) {
            return null;
        }

        return {
            blueprintId: this.#markerData.blueprintId || this.#blueprintId,
            id: this.#markerData.id,
            inspectionId: this.#markerData.inspectionId,
            pageNumber: this.#markerData.pageNumber,
            x: this.#markerData.x,
            y: this.#markerData.y
        };
    }

    /**
     * Generates a preview image from the marker viewer canvas showing only the area containing the marker.
     * The preview captures the canvas area with configurable padding around the marker.
     * @param {number} [padding=100] The padding in pixels around the marker bounds.
     * @param {string} [colorOverride] Optional CSS color used to draw the marker, overriding the computed `--marker-color`.
     * @returns {Promise<string|null>} Base64 data URL of the captured image, or null if marker is not present or canvas is unavailable.
     *
     * @memberof MarkerSelector
     */
    async getPreview(padding = 100, colorOverride = null) {
        // Only generate preview if marker is present.
        if (!this.#markerData || !this.#markerLayer) {
            return null;
        }

        // Navigate to the marker's page if not currently displayed.
        if (this.#markerData.pageNumber !== this.currentPage && this.#markerData.pageNumber <= this.totalPages) {
            await this.renderPage(this.#markerData.pageNumber);
        }

        // Resolve the page's CSS dimensions — the coordinate space the marker position lives in. In tile mode the base canvas is hidden and never
        // painted, so the size comes from the tile manifest; in PDF.js mode it comes from the visible canvas.
        const pageDimensions = this.#getPreviewPageDimensions();

        if (!pageDimensions) {
            return null;
        }

        const { pageCssWidth, pageCssHeight } = pageDimensions;

        const visibleMarker = this.#markerData;

        // Calculate bounds of the marker.
        const scaledX = visibleMarker.x * this.currentScale;
        const scaledY = visibleMarker.y * this.currentScale;

        // Account for marker size in bounds calculation.
        const markerHalfWidth = this.#markerRect.inlineSize / 2;
        const markerHeight = this.#markerRect.blockSize;

        const minX = scaledX - markerHalfWidth;
        const maxX = scaledX + markerHalfWidth;
        const minY = scaledY - markerHeight;
        const maxY = scaledY;

        // Conservative cross-browser canvas dimension cap.
        const MAX_CANVAS_DIMENSION = 8192;

        // Calculate square preview dimensions with edge compensation.
        const normalizeDimension = (value, fallback) => {
            if (!Number.isFinite(value) || value <= 0) {
                return fallback;
            }

            return Math.min(value, MAX_CANVAS_DIMENSION);
        };

        // Create a square preview of twice the padding size.
        const squareSize = 2 * padding;

        // Calculate the center point of the marker.
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // Calculate initial square bounds centered on marker.
        let squareMinX = centerX - squareSize / 2;
        let squareMinY = centerY - squareSize / 2;
        let squareMaxX = centerX + squareSize / 2;
        let squareMaxY = centerY + squareSize / 2;

        // Apply edge compensation: if square extends beyond the page, shift to available space.
        // Use CSS dimensions for comparison since our coordinates are in CSS pixels.
        if (squareMinX < 0) {
            squareMinX = 0;
            squareMaxX = Math.min(pageCssWidth, squareSize);
        }
        else if (squareMaxX > pageCssWidth) {
            squareMaxX = pageCssWidth;
            squareMinX = Math.max(0, pageCssWidth - squareSize);
        }

        if (squareMinY < 0) {
            squareMinY = 0;
            squareMaxY = Math.min(pageCssHeight, squareSize);
        }
        else if (squareMaxY > pageCssHeight) {
            squareMaxY = pageCssHeight;
            squareMinY = Math.max(0, pageCssHeight - squareSize);
        }

        // Ensure proper DPR scaling with transform reset.
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const boundingX = squareMinX;
        const boundingY = squareMinY;
        const rawWidth = squareMaxX - squareMinX;
        const rawHeight = squareMaxY - squareMinY;

        const boundingWidth = normalizeDimension(rawWidth, squareSize);
        const boundingHeight = normalizeDimension(rawHeight, squareSize);

        // Create a temporary canvas to capture the cropped area.
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.round(boundingWidth * dpr);
        tempCanvas.height = Math.round(boundingHeight * dpr);

        // Keep CSS size in CSS pixels.
        tempCanvas.style.width = `${boundingWidth}px`;
        tempCanvas.style.height = `${boundingHeight}px`;

        const tempContext = tempCanvas.getContext('2d');

        // Reset transform before scaling.
        tempContext.setTransform(1, 0, 0, 1, 0, 0);
        tempContext.scale(dpr, dpr);

        // Paint a white backdrop first: any region not covered by source pixels (and JPEG's transparent-to-black flattening) then reads as white
        // rather than black.
        tempContext.fillStyle = '#ffffff';
        tempContext.fillRect(0, 0, boundingWidth, boundingHeight);

        // Clip source draws to the preview bounds so off-crop tiles are cheap no-ops.
        tempContext.save();
        tempContext.beginPath();
        tempContext.rect(0, 0, boundingWidth, boundingHeight);
        tempContext.clip();

        if (this.#tileMode) {
            // Tile mode: the base canvas is hidden and empty, so composite the crop from the DOM tile <img> layers instead.
            await this.#drawTilesIntoPreview(tempContext, boundingX, boundingY, pageCssWidth, pageCssHeight);
        }
        else {
            // PDF.js mode: copy the crop straight from the visible canvas.
            const canvas = pageDimensions.canvas;

            // The source canvas has internal dimensions scaled by canvasPixelRatio; round to integer pixels to avoid sub-pixel rendering issues.
            const canvasPixelRatio = canvas.width / pageCssWidth || 1;
            const canvasBoundingX = Math.round(boundingX * canvasPixelRatio);
            const canvasBoundingY = Math.round(boundingY * canvasPixelRatio);
            const canvasRawWidth = Math.round(rawWidth * canvasPixelRatio);
            const canvasRawHeight = Math.round(rawHeight * canvasPixelRatio);

            tempContext.drawImage(
                canvas,
                canvasBoundingX, canvasBoundingY, canvasRawWidth, canvasRawHeight,
                0, 0, boundingWidth, boundingHeight
            );
        }

        tempContext.restore();

        // Draw the marker on the cropped canvas.
        // Pass CSS pixel offsets since the context is scaled by DPR.
        this.#drawMarkerOnCanvas(tempContext, visibleMarker, boundingX, boundingY, colorOverride);

        // Convert to base64 data URL.
        const dataUrl = tempCanvas.toDataURL('image/jpeg', 0.8);

        // Clean up temporary canvas to prevent memory leaks.
        tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCanvas.width = 0;
        tempCanvas.height = 0;

        return dataUrl;
    }

    /**
     * Resolves the current page's CSS dimensions for preview cropping. In tile mode the page size comes from the manifest (the base canvas is hidden
     * and unpainted); in PDF.js mode it comes from the visible canvas, which is also returned so the caller can read pixels straight off it.
     * @returns {?{pageCssWidth: number, pageCssHeight: number, canvas?: HTMLCanvasElement}} The page dimensions, or null when unavailable.
     *
     * @private
     * @memberof MarkerSelector
     */
    #getPreviewPageDimensions() {
        if (this.#tileMode) {
            const manifest = this.#tileManifest?.[this.currentPage - 1];

            if (!manifest) {
                return null;
            }

            return {
                pageCssHeight: manifest.pdfHeightPts * this.currentScale,
                pageCssWidth: manifest.pdfWidthPts * this.currentScale
            };
        }

        const canvas = this.canvas;

        if (!canvas) {
            return null;
        }

        // Use the canvas style directly instead of getComputedStyle to avoid issues with browser zoom.
        return {
            canvas,
            pageCssHeight: parseFloat(canvas.style.blockSize) || canvas.height,
            pageCssWidth: parseFloat(canvas.style.inlineSize) || canvas.width
        };
    }

    /**
     * Composites the preview crop from the mounted DOM tile `<img>` layers, mirroring the on-screen z-order: the whole-page base layer first
     * (guaranteed coverage) then the sharp detail layer on top. Each tile is positioned as a scale-independent percentage of the page, so its box maps
     * to CSS pixels via the page dimensions and then into the crop. The context is expected to be clipped to the preview bounds by the caller.
     * @param {CanvasRenderingContext2D} context The (DPR-scaled, clipped) preview context to draw into.
     * @param {number} offsetX The X offset of the crop area in CSS pixels.
     * @param {number} offsetY The Y offset of the crop area in CSS pixels.
     * @param {number} pageCssWidth The page width in CSS pixels at the current scale.
     * @param {number} pageCssHeight The page height in CSS pixels at the current scale.
     * @returns {Promise<void>} Resolves once the intersecting tiles have been composited.
     *
     * @private
     * @memberof MarkerSelector
     */
    async #drawTilesIntoPreview(context, offsetX, offsetY, pageCssWidth, pageCssHeight) {
        const tiles = [];

        // Base layer beneath, detail layer on top — same stacking the viewer shows.
        for (const layer of [this.#tileBaseLayer, this.#tileDetailLayer]) {
            if (layer) {
                tiles.push(...layer.querySelectorAll('img.pdf-tile'));
            }
        }

        // A not-yet-decoded <img> draws nothing, so wait for every tile to decode before compositing. Failed decodes are skipped below.
        await Promise.all(tiles.map(img => img.decode().catch(() => {})));

        for (const img of tiles) {
            if (!img.complete || !img.naturalWidth) {
                continue;
            }

            // Tiles are positioned as a percentage of the page; convert that box to CSS pixels, then into crop-relative coordinates.
            const fracX = parseFloat(img.style.insetInlineStart) / 100;
            const fracY = parseFloat(img.style.insetBlockStart) / 100;
            const fracW = parseFloat(img.style.inlineSize) / 100;
            const fracH = parseFloat(img.style.blockSize) / 100;

            if (!Number.isFinite(fracX) || !Number.isFinite(fracY) || !Number.isFinite(fracW) || !Number.isFinite(fracH)) {
                continue;
            }

            const destX = (fracX * pageCssWidth) - offsetX;
            const destY = (fracY * pageCssHeight) - offsetY;
            const destW = fracW * pageCssWidth;
            const destH = fracH * pageCssHeight;

            context.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, destX, destY, destW, destH);
        }
    }

    /**
     * Loads the saved marker on the viewer by marker ID.
     * Restores the previously saved marker when reopening a blueprint.
     * If marker data is provided, uses it directly; otherwise fetches from the server.
     * @param {string} markerId The unique identifier of the marker to load.
     * @param {object} [markerData] Optional marker data object to use instead of fetching from server.
     * @param {number} [markerData.blueprintId] The blueprint id associated with this marker.
     * @param {string} [markerData.id] The marker id.
     * @param {number} [markerData.inspectionId] The inspection id associated with this marker.
     * @param {number} [markerData.pageNumber] The page number where the marker should be placed.
     * @param {number} [markerData.x] The unscaled X coordinate of the marker.
     * @param {number} [markerData.y] The unscaled Y coordinate of the marker.
     *
     * @memberof MarkerSelector
     */
    async loadMarker(markerId, markerData = null) {
        if (!this.#blueprintId || !markerId) {
            this.setPlacementMode(false);

            return;
        }

        // Clear any existing marker.
        this.#markerData = null;
        this.#markerLayer.innerHTML = '';

        let marker = markerData;

        if (!marker) {
            try {
                // Fetch the marker data from the server.
                const response = await core.fetch({
                    url: `${baseUrl}/api/inspections/marker/${markerId}`,
                    config: { method: 'GET' }
                });

                if (!response.ok) {
                    console.warn('Failed to load marker:', markerId);
                    this.setPlacementMode(false);

                    return;
                }

                marker = await response.json();
            }
            catch (error) {
                console.warn('Error loading marker:', error);
                this.setPlacementMode(false);

                return;
            }
        }

        // Verify the marker belongs to the current blueprint.
        if (!marker || marker.blueprintId !== this.#blueprintId) {
            this.setPlacementMode(false);

            return;
        }

        const markerPage = marker.pageNumber;

        // Navigate to the marker's page first to prevent flickering.
        if (markerPage !== this.currentPage && markerPage <= this.totalPages) {
            await this.renderPage(markerPage);
        }

        // Add marker after navigating to the correct page.
        this.addMarker(marker);

        // Disable placement mode since a marker exists.
        this.setPlacementMode(false);

        // Apply read-only state to existing elements.
        this.#applyReadOnlyState();
    }

    /**
     * Removes a marker from the viewer.
     * @param {String} markerId The unique identifier of the marker to remove.
     * @param {Boolean} dispatch True to dispatch a 'markerRemoved' event, falsy if not.
     * 
     * @fires CustomEvent#markerRemoved
     * @memberof MarkerSelector
     */
    removeMarker(markerId, dispatch) {
        if (this.#markerData && this.#markerData.id === markerId) {
            // Remove the marker element from the DOM.
            this.#markerData.marker.remove();

            // Store reference before clearing.
            const markerData = this.#markerData;

            // Clear marker data.
            this.#markerData = null;

            // Clear id and reset button icon.
            if (this.#markerId === markerId) {
                this.#markerId = null;

                // Don't auto-enter placement mode, just reset to default state.
                this.setPlacementMode(false);

                // Update button to show add state.
                this.#updateMarkerButton();
            }

            // Should we dispatch.
            if (dispatch) {
                this.#dispatchMarkerEvent('markerRemoved', markerData);
            }
        }
    }

    /**
     * Overrides the base class's renderPage to update marker visibility when the page changes.
     * @param {number} pageNum The page number being rendered.
     *
     * @memberof MarkerSelector
     */
    async renderPage(pageNum) {
        await super.renderPage(pageNum);

        // If only one page, hide navigation.
        this.navigation.classList.toggle('hidden', this.totalPages === 1);

        // Update position and visibility for the marker.
        if (this.#markerData) {
            this.#markerData.marker.classList.toggle('hidden', this.#markerData.pageNumber !== pageNum);

            if (this.#markerData.pageNumber === pageNum) {
                this.#updateMarkerPosition(this.#markerData);
            }
        }
    }

    /**
     * Set the placement mode.
     * @param {Boolean} state True to enable placing, falsy to disable.
     * 
     * @memberof MarkerSelector
     */
    async setPlacementMode(state) {
        // Toggle placement mode.
        this.#isPlacingMarker = state;
        this.#markerButton.classList.toggle('active', this.#isPlacingMarker);

        // Set cursor.
        this.#markerLayer.style.cursor = this.#isPlacingMarker ? 'crosshair' : 'default';

        // Show message.
        this.classList.toggle('add-active', state);

        // Disable button.
        if (this.#isPlacingMarker) {
            this.#markerButton.setAttribute('disabled', '');
        }
        else {
            this.#markerButton.removeAttribute('disabled');
        }
    }

    /**
     * Overrides the base class's updateDimensionsOnly to update marker position on zoom/resize.
     *
     * @memberof MarkerSelector
     */
    updateDimensionsOnly() {
        super.updateDimensionsOnly();

        // The marker layer fills the wrapper via CSS (`inset: 0`), exactly like the tile layers — so it tracks panzoom's wrapper resize instantly, every
        // frame, with no JS. We must NOT pin it to an explicit pixel size here: that only updates one frame after panzoom resizes the wrapper, so during
        // wheel/pinch zoom the marker's percentage box would resolve against a stale layer and drift. The marker position itself is pure CSS percentage
        // too, so there's nothing to recompute per zoom step.
    }
    // #endregion ********************************************************************************/

    // #region: Render strategy (tile mode) ******************************************************/
    /**
     * Widens the base render-gate so the shared zoom/resize/render plumbing stays active in tile mode, where there is no PDF.js document.
     * @returns {boolean} True when a PDF.js document or a tile pyramid is available.
     *
     * @memberof MarkerSelector
     */
    hasRenderableSource() {
        return super.hasRenderableSource() || this.#tileMode;
    }

    /**
     * Render-strategy seam: blueprints with a tile context are rendered from the server-side tile pyramid instead of PDF.js. Probes the manifest,
     * triggers generation when needed, and polls until ready; on any failure it falls back to PDF.js and returns control to the base loader.
     * @param {string} src The source URL being loaded.
     * @returns {Promise<boolean>} True when tile mode has taken over the load.
     *
     * @memberof MarkerSelector
     */
    async loadSource(src) {
        // Reset any prior tile state before routing the new source.
        this.teardownSource();

        if (!src) {
            return false;
        }

        const tileContext = this.#parseTileContext(src);

        if (!tileContext) {
            return false;
        }

        this.#tileContext = tileContext;
        this.#tileMode = true;

        // Show the progress overlay immediately; the manifest probe can take long enough that a blank dialog reads as stuck.
        this.#showTileProgress({ currentPage: 0, currentPagePct: 0, pages: 0 });

        const initial = await this.#fetchTileManifest();

        if (!initial) {
            // Transport failure on the first probe — drop the overlay and let the base loader fall back to PDF.js.
            this.#tileMode = false;
            this.#tileContext = null;
            this.#hideTileProgress();

            return false;
        }

        if (initial.status === 'missing') {
            const triggered = await this.#triggerTileGeneration();

            await this.#handleTileStatus(triggered ?? initial);
        }
        else {
            await this.#handleTileStatus(initial);
        }

        // `#handleTileStatus` owns the terminal outcome — ready, still-generating (polling), or its own PDF.js fallback on 'failed'. Either way we've
        // taken over this load, so report it handled to keep the base loader from kicking off a duplicate PDF.js render.
        return true;
    }

    /**
     * Refreshes the visible region after a pan/zoom gesture. In tile mode this re-picks the pyramid level and mounts/unmounts detail tiles; otherwise
     * it defers to the base canvas/detail-overlay refresh.
     * @param {boolean} scaleChanged True when the gesture changed the scale (vs a pure pan).
     *
     * @memberof MarkerSelector
     */
    refreshViewport(scaleChanged) {
        if (this.#tileMode) {
            // A pan or a pinch both just shift/scale the visible region. The base layer keeps the page visible while the fresh tiles stream in.
            this.#scheduleTileViewportUpdate(PdfViewer.DEBOUNCE_DELAY);

            return;
        }

        super.refreshViewport(scaleChanged);
    }

    /**
     * Mounts detail tiles progressively while a pan/scroll gesture is in flight, coalesced to one update per animation frame. Without this, tiles only
     * appeared once the gesture settled (the debounced `refreshViewport`), so every region scrolled into stayed on the blurred base layer mid-gesture.
     * Mounting tiles is cheap — a DOM diff plus a few `<img>` appends, with already-decoded tiles reused from the retention cache — so a per-frame refresh
     * keeps the sharp layer following the scroll. No-op outside tile mode, where the base class owns the (costlier) canvas repaint.
     *
     * @memberof MarkerSelector
     */
    refreshViewportDuringPan() {
        if (!this.#tileMode || this.#tilePanFrame !== null) {
            return;
        }

        this.#tilePanFrame = window.requestAnimationFrame(() => {
            this.#tilePanFrame = null;

            if (!this.#tileMode || !this.#tileManifest) {
                return;
            }

            const manifest = this.#tileManifest[this.currentPage - 1];

            if (manifest) {
                this.#updateDetailTileLayer(manifest, this.currentPage);
            }
        });
    }

    /**
     * Renders the active page from tiles when in tile mode; otherwise defers to the base PDF.js render.
     * @param {number} pageNum One-based page number to render.
     * @returns {Promise<void>} Resolves once the page has been painted.
     *
     * @memberof MarkerSelector
     */
    async renderActivePage(pageNum) {
        if (this.#tileMode) {
            await this.#renderPageFromTiles(pageNum);

            return;
        }

        await super.renderActivePage(pageNum);
    }

    /**
     * Tears down the tile source: cancels timers/polling, clears the tile layers, hides the progress overlay, and resets the tile-mode flags. Invoked by
     * the base class on teardown and whenever the viewer falls back to the PDF.js path.
     *
     * @memberof MarkerSelector
     */
    teardownSource() {
        if (this.#tileViewportTimer !== null) {
            window.clearTimeout(this.#tileViewportTimer);
            this.#tileViewportTimer = null;
        }

        if (this.#tilePanFrame !== null) {
            window.cancelAnimationFrame(this.#tilePanFrame);
            this.#tilePanFrame = null;
        }

        this.#stopTilePolling();
        this.#stopFakeTileProgress();
        this.#clearTileLayers();
        this.#hideTileProgress();
        this.#hideTileMessage();

        this.#tileMode = false;
        this.#tileContext = null;
        this.#tileManifest = null;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Applies read-only state to all relevant elements.
     * 
     * @private
     * @memberof MarkerSelector
     */
    #applyReadOnlyState() {
        // Disable/enable marker button.
        if (this.readOnly) {
            this.#markerButton.setAttribute('disabled', '');
        }
        else {
            this.#markerButton.removeAttribute('disabled');
        }

        // Disable/enable blueprint dropdown.
        const blueprintSelector = document.querySelector('.blueprint-selector');

        if (blueprintSelector) {
            if (this.readOnly) {
                blueprintSelector.setAttribute('disabled', '');
            }
            else {
                blueprintSelector.removeAttribute('disabled');
            }
        }

        // Apply read-only styling to the marker.
        if (this.#markerData?.marker) {
            if (this.readOnly) {
                this.#markerData.marker.setAttribute('disabled', '');
            }
            else {
                this.#markerData.marker.removeAttribute('disabled');
            }
        }
    }

    /**
     * Converts a viewport client point to unscaled page coordinates by inverting the exact formula `#updateMarkerPosition` renders with: a fraction of
     * the live marker-layer rect, multiplied by the page's unscaled size. Deliberately avoids `currentScale` — panzoom captures its scale reference from
     * the already-fit-scaled wrapper, so `currentScale` does not equal the on-screen scale (`layerWidth / baseWidth`). Measuring straight off the rendered
     * layer keeps placement and dragging exact at any zoom.
     * @param {number} clientX Viewport X (e.g. `event.clientX`).
     * @param {number} clientY Viewport Y.
     * @returns {?{x: number, y: number}} Unscaled page coordinates, or null when the page size or layer rect isn't available yet.
     *
     * @private
     */
    #clientToPageCoords(clientX, clientY) {
        const base = this.getPageBaseSize(this.currentPage);

        if (!this.#markerLayer || !base || !base.baseWidth || !base.baseHeight) {
            return null;
        }

        const rect = this.#markerLayer.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        return {
            x: ((clientX - rect.left) / rect.width) * base.baseWidth,
            y: ((clientY - rect.top) / rect.height) * base.baseHeight
        };
    }

    /**
     * Dispatches marker-related events with consistent structure.
     *
     * @param {string} eventType The type of event to dispatch.
     * @param {object} markerData The marker data object.
     *
     * @private
     */
    #dispatchMarkerEvent(eventType, markerData) {
        this.dispatchEvent(new CustomEvent(eventType, {
            detail: {
                blueprintId: markerData.blueprintId || this.#blueprintId,
                id: markerData.id,
                inspectionId: markerData.inspectionId,
                pageNumber: markerData.pageNumber,
                x: markerData.x,
                y: markerData.y
            }
        }));
    }

    /**
     * Draws a location pin marker using native canvas drawing primitives.
     * Uses arc, lineTo and fill which work reliably across all browsers including iOS Safari.
     *
     * The pin shape matches the Material "location_on" icon: a circular head with
     * an inner circle cutout, tapering to a point at the bottom.
     *
     * @param {CanvasRenderingContext2D} context The canvas context to draw on.
     * @param {number} x The horizontal center position of the marker rect (CSS pixels).
     * @param {number} y The vertical bottom position of the marker rect (CSS pixels).
     * @param {number} width The marker width (CSS pixels).
     * @param {number} height The marker height (CSS pixels).
     * @param {string} color The fill color for the marker.
     *
     * @private
     * @memberof MarkerSelector
     */
    #drawLocationPin(context, x, y, width, height, color) {
        // Proportions derived from the Material "location_on" SVG (viewBox 0 -960 960 960).
        // Head circle: center (480, -560), outer radius 320, inner radius 80.
        // Tip: (480, -107). Mapped to normalized viewBox coordinates (0–1):
        const headLocalY = height * 0.417;
        const tipLocalY = height * 0.889;
        const outerRadius = width * 0.333;
        const innerRadius = width * 0.083;

        // Calculate the half-angle at the head center between the downward axis and the tangent point.
        const dist = tipLocalY - headLocalY;
        const halfAngle = Math.acos(outerRadius / dist);

        // Draw on a temporary canvas to handle the inner circle cutout cleanly.
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = Math.ceil(width);
        tempCanvas.height = Math.ceil(height);

        const tc = tempCanvas.getContext('2d');
        const localX = width / 2;

        // Draw the pin body: arc over the top of the head circle, then lines to the tip.
        // Arc goes counterclockwise from the right tangent point to the left tangent point.
        tc.beginPath();
        tc.arc(localX, headLocalY, outerRadius, Math.PI / 2 - halfAngle, Math.PI / 2 + halfAngle, true);
        tc.lineTo(localX, tipLocalY);
        tc.closePath();
        tc.fillStyle = color;
        tc.fill();

        // Cut out the inner circle using destination-out compositing.
        tc.globalCompositeOperation = 'destination-out';
        tc.beginPath();
        tc.arc(localX, headLocalY, innerRadius, 0, Math.PI * 2);
        tc.fill();

        // Draw the temp canvas onto the main context at the same position
        // as the original SVG image draw (bottom of rect at y, centered at x).
        context.drawImage(tempCanvas, x - width / 2, y - height);

        // Release temp canvas memory.
        tc.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        tempCanvas.width = 0;
        tempCanvas.height = 0;
    }

    /**
     * Draws the marker on the provided canvas context at the correct position relative to the crop area.
     * Uses native canvas primitives for reliable cross-browser rendering (including iOS Safari).
     * @param {CanvasRenderingContext2D} context The canvas context to draw on.
     * @param {object} marker The marker data object with x, y, and pageNumber properties.
     * @param {number} offsetX The X offset of the crop area in CSS pixels.
     * @param {number} offsetY The Y offset of the crop area in CSS pixels.
     * @param {string} [colorOverride] Optional color used for the marker fill instead of the computed `--marker-color`.
     *
     * @private
     * @memberof MarkerSelector
     */
    #drawMarkerOnCanvas(context, marker, offsetX, offsetY, colorOverride = null) {
        const markerColor = (colorOverride && colorOverride.trim())
            || getComputedStyle(this).getPropertyValue('--marker-color').trim()
            || '#4285F4';
        const markerWidth = this.#markerRect.inlineSize;
        const markerHeight = this.#markerRect.blockSize;

        // Calculate marker position in CSS pixels.
        // Marker coordinates are stored as unscaled positions, so scale them first.
        const markerCssX = marker.x * this.currentScale;
        const markerCssY = marker.y * this.currentScale;

        // Adjust for crop offset (also in CSS pixels).
        const x = markerCssX - offsetX;
        const y = markerCssY - offsetY;

        // Draw the location pin using native canvas primitives.
        // This avoids Path2D and SVG Image approaches that fail silently on iOS Safari.
        this.#drawLocationPin(context, x, y, markerWidth, markerHeight, markerColor);
    }

    /**
     * Sets up the viewer toolbar for markers, including the marker button and marker layer.
     *
     * @private
     */
    #setupMarkers() {
        // Access toolbar from the base class.
        if (!this.toolbar) {
            window.isDebugMode && console.warn('Viewer toolbar not found, cannot setup markers.');

            return;
        }

        // Calculate marker size only once.
        this.#markerRect = {
            blockSize: this.parseCssValue('--location-marker-block-size'),
            inlineSize: this.parseCssValue('--location-marker-inline-size')
        };
        
        // Add marker button.
        this.#markerButton = document.createElement('button');
        this.#markerButton.className = 'button button-tall marker-button';
        this.#markerButton.setAttribute('disabled', '');

        // Set initial icon and text based on current state.
        this.#updateMarkerButton();

        // Add marker button click handler.
        this.#markerButton.addEventListener('click', () => {
            if (this.#markerId) {
                // If marker exists, remove it (removeMarker will handle state reset).
                this.removeMarker(this.#markerId, true);
            }
            else {
                // Enter placement mode to add a marker.
                this.setPlacementMode(true);
            }
        }, { capture: false });

        // Check if a select is present for blueprints, if so move to the toolbar.
        const blueprintSelector = this.querySelector('.blueprint-selector');

        if (blueprintSelector) {
            this.toolbar.appendChild(blueprintSelector);
            blueprintSelector.classList.remove('hidden');
        }

        this.toolbar.appendChild(this.#markerButton);

        // Create marker layer if it doesn't exist.
        if (!this.#markerLayer) {
            this.#markerLayer = document.createElement('div');
            this.#markerLayer.className = 'location-marker-layer';
            this.#markerLayer.style.position = 'absolute';
            this.#markerLayer.style.insetBlockStart = '0';
            this.#markerLayer.style.insetInlineStart = '0';
            this.#markerLayer.style.inlineSize = '100%';
            this.#markerLayer.style.blockSize = '100%';
            this.#markerLayer.style.pointerEvents = 'auto';
            this.#markerLayer.style.cursor = 'default';

            if (this.wrapper) {
                this.wrapper.append(this.#markerLayer);
            }

            // Listen for marker added (both mouse and touch).
            this.#markerLayer.addEventListener('click', this.handleMarkerAdd, { capture: false });
            this.#markerLayer.addEventListener('touchend', this.handleMarkerAdd, { passive: false });
        }

        // Create notification element.
        const notification = document.createElement('div');

        notification.className = 'add-active-notification';
        notification.setAttribute('aria-live', 'polite');
        notification.textContent = this.#locales?.addActive || 'Toevoegen marker actief';

        this.appendChild(notification);
    }

    /**
     * Updates the marker button icon and label based on whether a marker exists.
     *
     * @private
     */
    #updateMarkerButton() {
        if (!this.#markerButton) {
            return;
        }

        const icon = this.#markerId ? 'lf-icon-location-off' : 'lf-icon-location-on';
        const label = this.#markerId ? (this.#locales?.deleteMarker || 'Verwijderen') : (this.#locales?.addMarker || 'Toevoegen marker');

        this.#markerButton.innerHTML = `<span class="tool-bar-icon ${icon}"></span><span class="tool-bar-label">${label}</span>`;
    }

    /**
     * Positions a marker as a percentage of the page, mirroring how the tile layer positions its tiles. Panzoom zooms by resizing the wrapper (not by a
     * CSS transform), so a percentage box rescales with the page for free during both pan and zoom — no per-frame JS and no lag behind the gesture, which
     * is exactly why pixel positioning (`x × currentScale`) drifted out of place while zooming. The page's unscaled dimensions come from the shared
     * viewport cache, populated by both the tile and PDF.js render paths. The center-bottom anchor offset lives in CSS (`transform: translate`), so the
     * pin keeps a constant on-screen size regardless of zoom.
     * @param {Object} markerData Position data for the marker.
     * @param {number} markerData.x The unscaled X coordinate of the marker.
     * @param {number} markerData.y The unscaled Y coordinate of the marker.
     * @param {number} markerData.pageNumber The page the marker belongs to.
     * @param {HTMLElement} markerData.marker The DOM element for the marker.
     *
     * @private
     */
    #updateMarkerPosition(markerData) {
        if (!markerData) {
            return;
        }

        const { x, y, marker } = markerData;
        const base = this.getPageBaseSize(markerData.pageNumber);

        marker.style.position = 'absolute';

        if (base && base.baseWidth && base.baseHeight) {
            // Page-fraction box: zoom-independent, so the wrapper resize scales the marker with the page automatically.
            marker.style.left = `${(x / base.baseWidth) * 100}%`;
            marker.style.top = `${(y / base.baseHeight) * 100}%`;
        }
        else {
            // Fallback before the page has been cached: scale by hand against the current zoom.
            marker.style.left = `${x * this.currentScale}px`;
            marker.style.top = `${y * this.currentScale}px`;
        }

        // We are only here for marker on the current page, this can be shown.
        markerData.marker.classList.remove('hidden');
    }
    // #endregion ********************************************************************************/

    // #region: Tile rendering *******************************************************************/
    /**
     * Builds a tile-endpoint URL for the current blueprint context, carrying the inspection guid through when public access is in play.
     * @param {string} suffix Endpoint-specific path suffix after `/api/blueprinttile/`.
     * @returns {string} The absolute URL.
     *
     * @private
     * @memberof MarkerSelector
     */
    #buildTileUrl(suffix) {
        const guid = this.#tileContext?.inspectionGuid;
        const query = guid ? `?inspectionGuid=${encodeURIComponent(guid)}` : '';

        return `${baseUrl}/api/blueprinttile/${suffix}${query}`;
    }

    /**
     * Removes every child of a tile layer. Plain `<img>` removal lets the browser reclaim the decoded bitmaps; there is no canvas backing store to free.
     * @param {HTMLDivElement} layer The tile layer to empty.
     *
     * @private
     * @memberof MarkerSelector
     */
    #clearLayer(layer) {
        if (!layer) {
            return;
        }

        while (layer.firstChild) {
            layer.removeChild(layer.firstChild);
        }
    }

    /**
     * Empties both tile layers and resets their bookkeeping. Called on source reset and teardown so a new blueprint starts from a clean slate.
     *
     * @private
     * @memberof MarkerSelector
     */
    #clearTileLayers() {
        this.#clearLayer(this.#tileBaseLayer);
        this.#clearLayer(this.#tileDetailLayer);
        this.#detailTiles.clear();
        this.#detailTileCache.clear();
        this.#tileBasePage = null;
    }

    /**
     * Computes the page-fit scale for a tile-mode page from its PDF-point dimensions and the current container size. Mirrors the `page-fit` branch of
     * `handleZoomSelectChange` but works directly off the manifest, so it can be used to size the very first render before the page-cache is populated.
     * @param {object} manifest The per-page manifest record.
     * @returns {number} The scale at which the page fits the container.
     *
     * @private
     * @memberof MarkerSelector
     */
    #computeFitScale(manifest) {
        const containerWidth = this.canvasContainer?.clientWidth || manifest.pdfWidthPts;
        const containerHeight = this.canvasContainer?.clientHeight || manifest.pdfHeightPts;

        if (!manifest.pdfWidthPts || !manifest.pdfHeightPts) {
            return 1;
        }

        return Math.min(containerWidth / manifest.pdfWidthPts, containerHeight / manifest.pdfHeightPts) || 1;
    }

    /**
     * Computes the tile-grid sub-rectangle that intersects the visible viewport, with `TILE_OVERSCAN` extra rings clamped to the grid.
     * @param {number} levelWidth Pixel width of the chosen pyramid level.
     * @param {number} levelHeight Pixel height of the chosen pyramid level.
     * @param {number} tileSize Pixel size of a single tile.
     * @param {number} cols Total tile columns at this level.
     * @param {number} rows Total tile rows at this level.
     * @returns {{tx0: number, ty0: number, tx1: number, ty1: number}} Half-open tile-grid bounds.
     *
     * @private
     * @memberof MarkerSelector
     */
    #computeVisibleTileBounds(levelWidth, levelHeight, tileSize, cols, rows) {
        // Before panzoom is set up the wrapper has no transform and no size; treat the whole page as visible.
        if (!this.panzoom || !this.canvasContainer) {
            return { tx0: 0, tx1: cols, ty0: 0, ty1: rows };
        }

        const containerRect = this.canvasContainer.getBoundingClientRect();
        const wrapperRect = this.wrapper.getBoundingClientRect();

        if (wrapperRect.width === 0 || wrapperRect.height === 0) {
            return { tx0: 0, tx1: cols, ty0: 0, ty1: rows };
        }

        const visibleLeft = Math.max(containerRect.left, wrapperRect.left);
        const visibleTop = Math.max(containerRect.top, wrapperRect.top);
        const visibleRight = Math.min(containerRect.right, wrapperRect.right);
        const visibleBottom = Math.min(containerRect.bottom, wrapperRect.bottom);

        if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
            // Wrapper fully off-screen; render the centre tile so a snap-back gets at least one tile, but skip the rest.
            const cx = Math.min(cols - 1, Math.floor(cols / 2));
            const cy = Math.min(rows - 1, Math.floor(rows / 2));

            return { tx0: cx, tx1: cx + 1, ty0: cy, ty1: cy + 1 };
        }

        const wrapperX0 = visibleLeft - wrapperRect.left;
        const wrapperY0 = visibleTop - wrapperRect.top;
        const wrapperX1 = visibleRight - wrapperRect.left;
        const wrapperY1 = visibleBottom - wrapperRect.top;

        const xRatio = levelWidth / wrapperRect.width;
        const yRatio = levelHeight / wrapperRect.height;

        const tx0 = Math.max(0, Math.floor((wrapperX0 * xRatio) / tileSize) - MarkerSelector.TILE_OVERSCAN);
        const ty0 = Math.max(0, Math.floor((wrapperY0 * yRatio) / tileSize) - MarkerSelector.TILE_OVERSCAN);
        const tx1 = Math.min(cols, Math.ceil((wrapperX1 * xRatio) / tileSize) + MarkerSelector.TILE_OVERSCAN);
        const ty1 = Math.min(rows, Math.ceil((wrapperY1 * yRatio) / tileSize) + MarkerSelector.TILE_OVERSCAN);

        return { tx0, tx1, ty0, ty1 };
    }

    /**
     * Builds the tile layers and the tile-generation progress overlay. Called from the constructor so the render-strategy hooks always have their DOM.
     * The marker layer is appended later in `#setupMarkers`, so it sits on top of the tiles.
     *
     * @private
     * @memberof MarkerSelector
     */
    #createTileElements() {
        // Tile layers live inside the wrapper alongside the base canvas so panzoom's sizing moves them together. The base layer (whole page, cheap
        // pyramid level) sits beneath the detail layer (visible region, sharp); both stay hidden until tile mode activates.
        this.#tileBaseLayer = document.createElement('div');
        this.#tileBaseLayer.className = 'pdf-tile-layer pdf-tile-base';
        this.#tileBaseLayer.hidden = true;
        this.#tileBaseLayer.setAttribute('role', 'document');
        this.#tileBaseLayer.setAttribute('aria-label', 'PDF page content');

        this.#tileDetailLayer = document.createElement('div');
        this.#tileDetailLayer.className = 'pdf-tile-layer pdf-tile-detail';
        this.#tileDetailLayer.hidden = true;
        this.#tileDetailLayer.setAttribute('aria-hidden', 'true');

        if (this.wrapper) {
            this.wrapper.append(this.#tileBaseLayer, this.#tileDetailLayer);
        }

        // Tile-generation progress overlay (hidden until a generation job is observed).
        this.#createTileProgressOverlay();
    }

    /**
     * Creates a single tile `<img>` positioned in percentages of its layer so it scales with the wrapper during pinch without per-frame JS. Edge tiles
     * are narrower/shorter than a full tile, so the box is derived from the tile's actual pixel extent within the level.
     * @param {object} manifest The per-page manifest record.
     * @param {number} pageNum One-based page number.
     * @param {number} level Pyramid level index.
     * @param {number} tx Tile column.
     * @param {number} ty Tile row.
     * @param {number} levelWidth Pixel width of this level.
     * @param {number} levelHeight Pixel height of this level.
     * @returns {HTMLImageElement} The positioned tile image element.
     *
     * @private
     * @memberof MarkerSelector
     */
    #createTileImg(manifest, pageNum, level, tx, ty, levelWidth, levelHeight) {
        const { tileSize } = manifest;
        const tileWidth = Math.min(tileSize, levelWidth - (tx * tileSize));
        const tileHeight = Math.min(tileSize, levelHeight - (ty * tileSize));

        const img = document.createElement('img');

        img.className = 'pdf-tile';
        img.decoding = 'async';
        img.draggable = false;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');

        // Percentage box: zoom-independent fraction of the page this tile covers, so panzoom's wrapper resize scales the tile for free.
        img.style.insetInlineStart = `${(tx * tileSize / levelWidth) * 100}%`;
        img.style.insetBlockStart = `${(ty * tileSize / levelHeight) * 100}%`;
        img.style.inlineSize = `${(tileWidth / levelWidth) * 100}%`;
        img.style.blockSize = `${(tileHeight / levelHeight) * 100}%`;

        img.src = this.#buildTileUrl(`${this.#tileContext.blueprintId}/${pageNum}/${level}/${tx}/${ty}.webp`);

        return img;
    }

    /**
     * Creates the tile-generation progress overlay inside the canvas container.
     * Stays hidden until `#showTileProgress` is called; removed from display as soon as tiles are ready.
     *
     * @private
     * @memberof MarkerSelector
     */
    #createTileProgressOverlay() {
        this.#tileProgressOverlay = document.createElement('div');
        this.#tileProgressOverlay.className = 'tile-progress-overlay';
        this.#tileProgressOverlay.setAttribute('aria-live', 'polite');
        this.#tileProgressOverlay.hidden = true;

        this.#tileProgressLabel = document.createElement('div');
        this.#tileProgressLabel.className = 'tile-progress-label';

        const track = document.createElement('div');
        track.className = 'tile-progress-track';
        this.#tileProgressBar = document.createElement('div');
        this.#tileProgressBar.className = 'tile-progress-bar';
        track.append(this.#tileProgressBar);

        this.#tileProgressOverlay.append(this.#tileProgressLabel, track);
        this.canvasContainer.append(this.#tileProgressOverlay);

        // Terminal-message overlay (hidden until a blueprint is found to be undisplayable). Reuses the progress overlay's centred layout.
        this.#tileMessageOverlay = document.createElement('div');
        this.#tileMessageOverlay.className = 'tile-message-overlay';
        this.#tileMessageOverlay.setAttribute('role', 'alert');
        this.#tileMessageOverlay.hidden = true;
        this.canvasContainer.append(this.#tileMessageOverlay);
    }

    /**
     * Builds the whole-page base tile layer at the cheapest pyramid level (level 0). Skipped when the layer already holds this page, so a pan or zoom
     * never rebuilds it. The base level is a handful of tiles, so mounting the full grid is cheap and gives every region an instant low-res fallback.
     * @param {object} manifest The per-page manifest record.
     * @param {number} pageNum One-based page number.
     *
     * @private
     * @memberof MarkerSelector
     */
    #ensureBaseTileLayer(manifest, pageNum) {
        if (this.#tileBasePage === pageNum) {
            return;
        }

        const level = 0;
        const scaleDown = 2 ** (manifest.maxZoom - level);
        const levelWidth = Math.max(1, Math.floor(manifest.width / scaleDown));
        const levelHeight = Math.max(1, Math.floor(manifest.height / scaleDown));
        const { tileSize } = manifest;
        const cols = Math.ceil(levelWidth / tileSize);
        const rows = Math.ceil(levelHeight / tileSize);

        this.#clearLayer(this.#tileBaseLayer);

        for (let ty = 0; ty < rows; ty++) {
            for (let tx = 0; tx < cols; tx++) {
                this.#tileBaseLayer.append(this.#createTileImg(manifest, pageNum, level, tx, ty, levelWidth, levelHeight));
            }
        }

        this.#tileBasePage = pageNum;
    }

    /**
     * Fetches the current manifest/status for the active blueprint. Returns null on network or auth failure (caller falls back to PDF.js rendering).
     * @returns {Promise<?object>} The parsed status response, or null.
     *
     * @private
     * @memberof MarkerSelector
     */
    async #fetchTileManifest() {
        try {
            const response = await core.fetch({
                config: { method: 'GET' },
                url: this.#buildTileUrl(`manifest/${this.#tileContext.blueprintId}`)
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        }
        catch (error) {
            window.isDebugMode && console.warn('Tile manifest fetch failed:', error);

            return null;
        }
    }

    /**
     * Starts or continues the tile-generation polling loop. Updates the progress overlay and, when ready, initiates the first page render.
     * @param {object} status The latest status response from the server.
     * @returns {Promise<void>} Resolves once the terminal state (ready or failed) has been handled.
     *
     * @private
     * @memberof MarkerSelector
     */
    async #handleTileStatus(status) {
        // Hand off from the generic loader to the tile-specific feedback as soon as we know the state. The generic loader sets `opacity: 0` on the
        // canvas container which would otherwise hide the progress overlay and the eventual tile layers.
        this.toggleLoader(false);

        if (status.status === 'ready') {
            this.#stopTilePolling();
            this.#hideTileProgress();
            this.#tileManifest = status.pageManifests;
            this.totalPages = this.#tileManifest.length;
            this.currentPage = 1;
            this.clearPageViewportCache();

            // Destroy any Panzoom left over from a previously-shown blueprint. It captured that blueprint's page size as
            // its contain/original reference, so reusing it for a blueprint of a different aspect ratio makes the drawing
            // jump on the first pan/zoom. Destroying it here (before the first render) lets renderPageFromTiles size the
            // wrapper to this blueprint's page and setupPanzoom below re-capture the correct base. (The PDF.js path does
            // the same on load.)
            this.destroyPanzoom();

            // Tile pyramid is fully loaded — the canonical "tile mode active" point. Done before rendering so the render-mode label is correct from the
            // very first frame the user sees.
            this.setRenderMode('tile');

            // Show the tile layers (and hide the PDF.js canvas) before the first paint.
            this.#setTileLayersActive(true);

            // Render the first page already at page-fit. Rendering at scale 1 first would make the detail layer mount the whole page at full pyramid
            // resolution (thousands of <img> tiles) for the one frame before page-fit shrinks it — fitting up front keeps the initial mount bounded.
            this.currentScale = this.getValidScale(this.#computeFitScale(this.#tileManifest[0]));

            await this.#renderPageFromTiles(this.currentPage);

            if (!this.panzoom) {
                this.setupPanzoom();
            }

            await this.handleZoomSelectChange('page-fit');

            if (this.navigation) {
                this.navigation.classList.add('visible');
            }

            this.updateContentPager();
            this.dispatchEvent(new CustomEvent('pdf-loaded', { detail: { numPages: this.totalPages } }));

            return;
        }

        if (status.status === 'generating') {
            this.#showTileProgress(status);
            this.#scheduleTilePoll();

            return;
        }

        // 'missing' shortly after a POST/generate returned 202 is a normal transient; keep polling.
        if (status.status === 'missing') {
            this.#showTileProgress(status);
            this.#scheduleTilePoll();

            return;
        }

        // 'failed': tile generation gave up on the server.
        this.#stopTilePolling();
        this.#hideTileProgress();
        this.#tileMode = false;
        window.isDebugMode && console.warn('Tile generation failed:', status.errorMessage);

        // On iOS the PDF.js fallback rasterises the whole page to a canvas, which a blueprint big enough to need (and fail) tiling will overrun — the
        // device clamps the canvas and the page renders garbled and jumps while panning. Tiling is the iOS-safe path, so its failure means we cannot show
        // the drawing here; surface a clear message instead of a broken render. Other platforms have the canvas headroom to fall back to PDF.js.
        if (this.isIos) {
            this.#showTileMessage(this.#locales?.fileTooLarge || 'This file is too large to display on this device.');

            return;
        }

        await this.loadWithPdfjs(this.getAttribute('src'));
    }

    /**
     * Hides the terminal-message overlay so a subsequent (re)load starts clean.
     *
     * @private
     * @memberof MarkerSelector
     */
    #hideTileMessage() {
        if (this.#tileMessageOverlay) {
            this.#tileMessageOverlay.hidden = true;
            this.#tileMessageOverlay.textContent = '';
        }
    }

    /**
     * Hides the tile-generation progress overlay and resets the bar so the next load starts fresh.
     *
     * @private
     * @memberof MarkerSelector
     */
    #hideTileProgress() {
        if (!this.#tileProgressOverlay) {
            return;
        }

        this.#tileProgressOverlay.hidden = true;
        this.canvasContainer.classList.remove('tile-generating');

        // Cancel the fake-progress creep and reset the displayed bar so the next load starts fresh from 0% rather than picking up where the previous
        // load left off.
        this.#stopFakeTileProgress();
        this.#tileFakeProgressValue = 0;

        if (this.#tileProgressBar) {
            this.#tileProgressBar.style.inlineSize = '';
        }
    }

    /**
     * Renders the bar width and label using the current `#tileFakeProgressValue`. Single render path shared by `#showTileProgress`
     * (when a status response arrives) and the fake-progress tick (between responses), so the bar and the label stay in lock-step regardless of which
     * caller triggered the update.
     *
     * @private
     * @memberof MarkerSelector
     */
    #paintTileProgress() {
        if (!this.#tileProgressBar || !this.#tileProgressLabel) {
            return;
        }

        const drawn = this.#tileFakeProgressValue;
        const template = resources?.pdfViewer?.preparingBlueprint || 'Preparing blueprint… page {0} of {1} ({2}%)';

        // Single, stable label throughout the load. We slice the template at the leading `…` (every translation puts the page-counter behind it) so
        // the text reads as the natural prefix in any locale ("Preparing blueprint…", "Blauwdruk voorbereiden…", etc.) without leaking placeholder
        // dashes or a mid-load format switch into the user's view. The bar carries the progress; the label just labels what's progressing.
        const ellipsisIdx = template.indexOf('…');
        const labelText = ellipsisIdx >= 0 ? template.slice(0, ellipsisIdx + 1) : template.split('{')[0].trim();

        this.#tileProgressBar.style.inlineSize = `${drawn}%`;
        this.#tileProgressLabel.textContent = labelText;
    }

    /**
     * Parses the blueprint id and optional inspection guid from the src URL (or, as a fallback, the `blueprint-id` attribute) so the tile endpoint can be
     * addressed.
     *
     * The src wins over the attribute: it is the resource actually being loaded, and switching drawings sets `src` before `blueprint-id`. Reading the
     * attribute first therefore picked up the *previous* blueprint id (the attribute lags `src` by one `setAttribute`), loading the wrong — off-by-one —
     * drawing. The attribute fallback remains for sources that don't carry the id in their URL.
     * @param {string} src The current `src` attribute value.
     * @returns {?{blueprintId: number, inspectionGuid: ?string}} The tile context, or null when no blueprint id can be resolved.
     *
     * @private
     * @memberof MarkerSelector
     */
    #parseTileContext(src) {
        if (!src) {
            return null;
        }

        let srcUrl;
        try {
            srcUrl = new URL(src, window.location.origin);
        }
        catch {
            return null;
        }

        const inspectionGuid = srcUrl.searchParams.get('inspectionGuid');

        // Preferred: the GetBlueprint URL pattern carries the id as the last path segment — authoritative for the source being loaded.
        const match = srcUrl.pathname.match(/\/api\/inspections\/blueprint\/(\d+)$/);

        if (match) {
            return { blueprintId: parseInt(match[1], 10), inspectionGuid };
        }

        // Fallback: an explicit blueprint-id attribute, for sources that don't embed the id in their URL.
        const attrValue = this.getAttribute('blueprint-id');

        if (attrValue && (/^\d+$/).test(attrValue)) {
            return { blueprintId: parseInt(attrValue, 10), inspectionGuid };
        }

        return null;
    }

    /**
     * Chooses the best server-side zoom level for a given page at the current display scale. Picks the smallest level whose pixel width meets or
     * exceeds what the displayed page needs, so the rendered result stays sharp without over-fetching. The DOM tile layer only mounts the visible tiles
     * of the chosen level, so there is no whole-page canvas to cap — picking the full-detail level at high zoom costs only the on-screen tiles.
     * @param {object} manifest The per-page manifest record.
     * @returns {number} The zoom-level index to request.
     *
     * @private
     * @memberof MarkerSelector
     */
    #pickTileZoomLevel(manifest) {
        // Cap the ratio (not the wider zoom): keeps DPR-3 phones from mounting ~4× the tiles for sharpness the eye can't resolve on a blueprint.
        const pixelRatio = Math.min(window.devicePixelRatio || 1, MarkerSelector.TILE_MAX_DPR);

        // Target pixel width of the displayed page at the current scale.
        const desiredWidth = manifest.pdfWidthPts * this.currentScale * pixelRatio;

        for (let level = 0; level <= manifest.maxZoom; level++) {
            const levelWidth = manifest.width / (2 ** (manifest.maxZoom - level));

            if (levelWidth >= desiredWidth) {
                return level;
            }
        }

        return manifest.maxZoom;
    }

    /**
     * Renders the given page from server-generated tiles using two DOM `<img>` layers: a cheap whole-page base layer for instant fallback content, and
     * a sharp detail layer holding the visible region at the zoom-appropriate pyramid level. No canvas is used, so the device canvas-pixel limit — the
     * iOS crash on large blueprints — never applies, and deep zoom stays sharp because only the on-screen tiles of the chosen level are mounted.
     * @param {number} pageNum One-based page number.
     * @returns {Promise<void>} Resolves once the tile layers for this page/viewport have been mounted.
     *
     * @private
     * @memberof MarkerSelector
     */
    async #renderPageFromTiles(pageNum) {
        if (!this.#tileManifest) {
            return;
        }

        const manifest = this.#tileManifest[pageNum - 1];

        if (!manifest) {
            return;
        }

        const pageCssWidth = manifest.pdfWidthPts * this.currentScale;
        const pageCssHeight = manifest.pdfHeightPts * this.currentScale;

        // Populate the viewport cache so downstream zoom logic (updateDimensionsOnly, handleZoomSelectChange) has the page dimensions it expects.
        this.cachePageViewport(pageNum, manifest.pdfWidthPts, manifest.pdfHeightPts);

        // Only size the wrapper before panzoom takes over; afterward panzoom owns wrapper sizing via `updateDimensionsOnly`, and writing here would
        // snap it back to a stale scale if the user zoomed during this render. The tile layers fill the wrapper (CSS inset:0), so they follow its size.
        if (!this.panzoom) {
            this.wrapper.style.inlineSize = `${pageCssWidth}px`;
            this.wrapper.style.blockSize = `${pageCssHeight}px`;
        }

        // Base layer: whole page at the cheapest level, rebuilt only on a page change. Stays beneath the detail layer as a no-blank-frame fallback.
        this.#ensureBaseTileLayer(manifest, pageNum);

        // Detail layer: the visible region (plus overscan) at the zoom-appropriate level.
        this.#updateDetailTileLayer(manifest, pageNum);

        this.updateContentPager();
        this.updateZoomControlValue(this.currentScale);

        this.dispatchEvent(new CustomEvent('page-rendered', {
            detail: {
                pageNumber: pageNum,
                scale: this.currentScale
            }
        }));
    }

    /**
     * Stores a scrolled-out detail tile in the LRU cache, refreshing its recency and evicting the oldest entries past `TILE_CACHE_MAX`. Evicted tiles
     * keep no reference, so the browser is free to reclaim their decoded bitmaps.
     * @param {string} key The tile key (`${pageNumber}/${level}/${tx}/${ty}`).
     * @param {HTMLImageElement} img The detached tile image to retain.
     *
     * @private
     * @memberof MarkerSelector
     */
    #retainDetailTile(key, img) {
        // Re-insert at the tail so this tile counts as most-recently used.
        if (this.#detailTileCache.has(key)) {
            this.#detailTileCache.delete(key);
        }

        this.#detailTileCache.set(key, img);

        // Evict least-recently-used entries (Map iterates in insertion order, so the first key is the oldest).
        while (this.#detailTileCache.size > MarkerSelector.TILE_CACHE_MAX) {
            const oldestKey = this.#detailTileCache.keys().next().value;

            this.#detailTileCache.delete(oldestKey);
        }
    }

    /**
     * Schedules the next manifest poll. Idempotent.
     *
     * @private
     * @memberof MarkerSelector
     */
    #scheduleTilePoll() {
        if (this.#tilePollTimer !== null) {
            return;
        }

        this.#tilePollTimer = window.setTimeout(async () => {
            this.#tilePollTimer = null;

            if (!this.#tileMode) {
                return;
            }

            const status = await this.#fetchTileManifest();

            if (!status) {
                // Fall back to PDF.js on a transport failure.
                this.#tileMode = false;
                this.#hideTileProgress();
                await this.loadWithPdfjs(this.getAttribute('src'));

                return;
            }

            await this.#handleTileStatus(status);
        }, MarkerSelector.TILE_POLL_MS);
    }

    /**
     * Debounced viewport refresh for tile mode. Coalesces the burst of events at the end of a pan or pinch into one mount/unmount pass over the detail
     * layer for the current page.
     * @param {number} delay Debounce delay in ms.
     *
     * @private
     * @memberof MarkerSelector
     */
    #scheduleTileViewportUpdate(delay) {
        if (this.#tileViewportTimer !== null) {
            window.clearTimeout(this.#tileViewportTimer);
        }

        this.#tileViewportTimer = window.setTimeout(() => {
            this.#tileViewportTimer = null;

            if (!this.#tileMode || !this.#tileManifest) {
                return;
            }

            const manifest = this.#tileManifest[this.currentPage - 1];

            if (manifest) {
                this.#updateDetailTileLayer(manifest, this.currentPage);
            }
        }, Math.max(0, delay));
    }

    /**
     * Shows or hides the tile layers, flipping the PDF.js base canvas to the opposite visibility. Tile mode shows the layers and hides the canvas; the
     * PDF.js path does the reverse so a source that falls back from tiles renders cleanly.
     * @param {boolean} active True to show tile layers (tile mode), false to restore the canvas (PDF.js mode).
     *
     * @private
     * @memberof MarkerSelector
     */
    #setTileLayersActive(active) {
        this.setBaseCanvasVisible(!active);

        if (this.#tileBaseLayer) {
            this.#tileBaseLayer.hidden = !active;
        }

        if (this.#tileDetailLayer) {
            this.#tileDetailLayer.hidden = !active;
        }
    }

    /**
     * Shows the terminal-message overlay with the given text, hiding the progress overlay and tile layers so it stands alone. Used when a blueprint cannot
     * be displayed at all (see `#tileMessageOverlay`).
     * @param {string} text The (already-localised) message to display.
     *
     * @private
     * @memberof MarkerSelector
     */
    #showTileMessage(text) {
        this.#hideTileProgress();
        this.#setTileLayersActive(false);
        this.toggleLoader(false);

        // Nothing to mark on an undisplayable blueprint.
        this.#markerButton?.setAttribute('disabled', '');

        if (this.#tileMessageOverlay) {
            this.#tileMessageOverlay.textContent = text;
            this.#tileMessageOverlay.hidden = false;
        }
    }

    /**
     * Shows and updates the progress overlay based on the given status response.
     * @param {object} status The latest status response.
     *
     * @private
     * @memberof MarkerSelector
     */
    #showTileProgress(status) {
        if (!this.#tileProgressOverlay) {
            return;
        }

        const pages = status.pages ?? 0;
        const currentPage = status.currentPage ?? 0;
        const pct = status.currentPagePct ?? 0;
        const realOverall = pages > 0 ? Math.min(100, Math.round((((currentPage - 1) / pages) * 100) + (pct / pages))) : 0;

        // Forward-only: the displayed value never drops below the fake creep, so transient out-of-order status responses can't make the bar (or the
        // label percentage) move backward.
        const drawn = Math.max(this.#tileFakeProgressValue, realOverall);

        this.#tileFakeProgressValue = drawn;

        // Stop the fake creep once real progress has caught up — beyond this point the server drives both bar and label.
        if (pages > 0 && realOverall >= drawn) {
            this.#stopFakeTileProgress();
        }
        else {
            this.#startFakeTileProgress();
        }

        this.#paintTileProgress();
        this.#tileProgressOverlay.hidden = false;
        this.canvasContainer.classList.add('tile-generating');
    }

    /**
     * Starts (or no-ops if already running) the fake-progress creep that ticks the bar up by 1% every `TILE_PROGRESS_FAKE_TICK_MS` until either real
     * server progress overtakes it or the cap (`TILE_PROGRESS_FAKE_CAP`) is reached. Each tick re-renders both the bar and the label.
     *
     * @private
     * @memberof MarkerSelector
     */
    #startFakeTileProgress() {
        if (this.#tileFakeProgressTimer !== null || !this.#tileProgressBar) {
            return;
        }

        // Begin at 1% on the very first show so the user sees a non-zero bar immediately, before the first tick fires.
        if (this.#tileFakeProgressValue < 1) {
            this.#tileFakeProgressValue = 1;
            this.#paintTileProgress();
        }

        this.#tileFakeProgressTimer = window.setInterval(() => {
            if (this.#tileFakeProgressValue >= MarkerSelector.TILE_PROGRESS_FAKE_CAP) {
                this.#stopFakeTileProgress();

                return;
            }

            this.#tileFakeProgressValue += 1;
            this.#paintTileProgress();
        }, MarkerSelector.TILE_PROGRESS_FAKE_TICK_MS);
    }

    /**
     * Stops the fake-progress creep without resetting the displayed value, so a subsequent real-progress update can pick up from the last drawn
     * width and continue smoothly forward.
     *
     * @private
     * @memberof MarkerSelector
     */
    #stopFakeTileProgress() {
        if (this.#tileFakeProgressTimer !== null) {
            window.clearInterval(this.#tileFakeProgressTimer);
            this.#tileFakeProgressTimer = null;
        }
    }

    /**
     * Stops the polling loop if one is active.
     *
     * @private
     * @memberof MarkerSelector
     */
    #stopTilePolling() {
        if (this.#tilePollTimer !== null) {
            window.clearTimeout(this.#tilePollTimer);
            this.#tilePollTimer = null;
        }
    }

    /**
     * Triggers generation for the current blueprint if it is not already running. Returns the status response the server reported.
     * @returns {Promise<?object>} The status response, or null on failure.
     *
     * @private
     * @memberof MarkerSelector
     */
    async #triggerTileGeneration() {
        try {
            const response = await core.fetch({
                config: { method: 'POST' },
                url: this.#buildTileUrl(`generate/${this.#tileContext.blueprintId}`)
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        }
        catch (error) {
            window.isDebugMode && console.warn('Tile generate trigger failed:', error);

            return null;
        }
    }

    /**
     * Mounts/unmounts detail tiles so the layer holds exactly the visible region (plus an overscan ring) at the zoom-appropriate pyramid level. Keys
     * include the page and level, so a page change or zoom-level switch naturally swaps the whole set while the base layer shows through underneath.
     * @param {object} manifest The per-page manifest record.
     * @param {number} pageNum One-based page number.
     *
     * @private
     * @memberof MarkerSelector
     */
    #updateDetailTileLayer(manifest, pageNum) {
        const level = this.#pickTileZoomLevel(manifest);
        const scaleDown = 2 ** (manifest.maxZoom - level);
        const levelWidth = Math.max(1, Math.floor(manifest.width / scaleDown));
        const levelHeight = Math.max(1, Math.floor(manifest.height / scaleDown));
        const { tileSize } = manifest;
        const cols = Math.ceil(levelWidth / tileSize);
        const rows = Math.ceil(levelHeight / tileSize);
        const bounds = this.#computeVisibleTileBounds(levelWidth, levelHeight, tileSize, cols, rows);

        const needed = new Set();

        for (let ty = bounds.ty0; ty < bounds.ty1; ty++) {
            for (let tx = bounds.tx0; tx < bounds.tx1; tx++) {
                needed.add(`${pageNum}/${level}/${tx}/${ty}`);
            }
        }

        // Unmount tiles that are no longer needed (different page/level, or scrolled out of the overscan ring). Detach but retain them in the LRU cache so
        // scrolling back re-attaches an already-decoded tile instead of mounting a fresh one that flashes the blurred base layer while it re-decodes.
        for (const [key, img] of this.#detailTiles) {
            if (!needed.has(key)) {
                img.remove();
                this.#detailTiles.delete(key);
                this.#retainDetailTile(key, img);
            }
        }

        // Mount newly-visible tiles, reusing a retained (already-decoded) tile from the cache when one exists for this key.
        for (let ty = bounds.ty0; ty < bounds.ty1; ty++) {
            for (let tx = bounds.tx0; tx < bounds.tx1; tx++) {
                const key = `${pageNum}/${level}/${tx}/${ty}`;

                if (!this.#detailTiles.has(key)) {
                    let img = this.#detailTileCache.get(key);

                    if (img) {
                        this.#detailTileCache.delete(key);
                    }
                    else {
                        img = this.#createTileImg(manifest, pageNum, level, tx, ty, levelWidth, levelHeight);
                    }

                    this.#detailTiles.set(key, img);
                    this.#tileDetailLayer.append(img);
                }
            }
        }
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'marker-selector' and maps it to the MarkerSelector class.
 */
customElements.define(MarkerSelector.tagName, MarkerSelector);
